
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import CurrencyAmountField from '../../../../../framework/fields/CurrencyAmountField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import { getAccountOptions } from '../../../../service/application_options_service';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { errorToast, successToast } from '../../../../service/toast_service';
import { VendorContract } from '../../../../../../common/application/model/VendorContract';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { AccountType } from '../../../../../../common/application/model/enums/AccountType';
import { VendorContractPrefillResponse } from '../../../../../../common/application/model/api/VendorContractPrefillResponse';
import { getEnumerationOptions } from '../../../../../framework/service/options';
import { VendorContractScope } from '../../../../../../common/application/model/enums/VendorContractScope';
import AutoCompleteField from '../../../../../framework/fields/AutoCompleteField.vue';
import { getVendorContractPrefillData, postAddQuickVendorContract } from '../../../../client/vehicle_contract_client';
import { QuickAddVendorContractRequest } from '../../../../../../common/application/model/api/QuickAddVendorContractRequest';

@Component({
    components: {
        ValidationObserver,
        CurrencyAmountField,
        SelectField,
        AutoCompleteField,
    },
})
export default class QuickAddVendorContractModal extends Vue {
    @Prop({ type: Boolean }) isActive!: boolean;
    @Prop({ required: true }) readonly purchaseInvoiceId!: string;
    @Prop({ required: true }) readonly invoiceRowId!: string;

    readonly resourceType = ApplicationResource.VENDOR_CONTRACT;

    local = {
        row: {
            payment: undefined as number | undefined,
            customerExpenseAccountId: undefined as string | undefined,
            vendorLocationId: undefined as string | undefined,
            vendorContractType: undefined as VendorContractType | undefined,
            vendorId: undefined as string | undefined, // ✅ Add this
        },
        customerExpenseAccountOptions: [] as { id: string | undefined; label: string }[],
        vendorLocationParams: new Map() as Map<string, string>,
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    localPrefillData: VendorContractPrefillResponse | undefined = undefined;

    @Watch('isActive')
    async onModalOpen(isActive: boolean) {
        if (isActive) {
            await this.loadPrefillData();
        }
    }

    async loadPrefillData() {
        try {
            const data = await getVendorContractPrefillData(this.purchaseInvoiceId, this.invoiceRowId);
            this.localPrefillData = data;
            await this.onPrefillDataChanged(data);
        } catch (e) {
            errorToast(this, 'message.failedToLoadPrefillData');
        }
    }
    async onPrefillDataChanged(newData?: VendorContractPrefillResponse) {
        if (!newData) return;

        if (newData.customerId) {
            try {
                this.local.customerExpenseAccountOptions = await getAccountOptions(
                    newData.customerId,
                    AccountType.EXPENSE,
                );
            } catch (err) {
                console.error('Failed to load account options:', err);
            }
        }

        if (newData.vendorId) {
            this.local.vendorLocationParams = new Map([['parentId', newData.vendorId]]);
        }

        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );

        this.$set(this.local, 'row', {
            payment: newData.payment ?? 0,
            customerExpenseAccountId: newData.customerExpenseAccountId,
            vendorLocationId: newData.vendorLocationId,
            vendorContractType: newData.vendorContractType as VendorContractType,
            vendorId: newData.vendorId, // ✅ Add this
        });
    }

    async add() {
        if (
            !this.localPrefillData ||
            this.local.row.payment === undefined ||
            this.local.row.payment < 0 ||
            !this.local.row.vendorContractType
        ) {
            errorToast(this, 'message.missingRequiredFields');
            return;
        }

        const vendorContract: VendorContract = {
            id: '',
            created: new Date(),
            modified: new Date(),
            customerExpenseAccountId: this.local.row.customerExpenseAccountId,
            customerId: this.localPrefillData.customerId!,
            vehicleContractId: this.localPrefillData.vehicleContractId!,
            vendorLocationId: this.local.row.vendorLocationId!,
            vendorId: this.localPrefillData.vendorId!,
            budgetType: this.localPrefillData.budgetType,
            contractNumber: '',
            endDate: new Date(this.localPrefillData.endDate!),
            firstPaymentDueDate: new Date(this.localPrefillData.firstPaymentDueDate!),
            startDate: new Date(this.localPrefillData.startDate!),
            payment: this.local.row.payment!,
            paymentType: this.localPrefillData.paymentType,
            vehicleRegistrationNumber: this.localPrefillData.vehicleRegistrationNumber!,
            vendorContractScope: VendorContractScope.UNSPECIFIED,
            vendorContractType: this.local.row.vendorContractType!,
            vendorNumber: this.localPrefillData.vendorNumber!,
        };

        try {
            const request: QuickAddVendorContractRequest = {
                vendorContract: vendorContract,
                invoiceRowId: this.localPrefillData.invoiceRowId,
            };
            await postAddQuickVendorContract(request);
            successToast(this, 'message.vendorContractSaved');
            this.$emit('refresh');
        } catch (error) {
            errorToast(this);
        }

        this.$emit('update:isActive', false);
    }
}
